import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { BookabilityDecision } from "@src/appV2/OpenShifts/ShiftAction";
import {
  GET_BOOKABILITY_STATUS_URL,
  useBookabilityStatus,
} from "@src/appV2/OpenShifts/ShiftAction/api/useBookabilityStatus";
import { useGetShift } from "@src/appV2/Shifts/Shift/api/useGetShift";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useGetWorkplaceQuiz } from "@src/appV2/WorkplaceQuiz/api/useGetWorkplaceQuiz/useGetWorkplaceQuiz";
import { useSubmitWorkplaceQuiz } from "@src/appV2/WorkplaceQuiz/api/useSubmitWorkplaceQuiz/useSubmitWorkplaceQuiz";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { WorkplaceNoteIdentifier } from "../Workplace/constants";
import { WorkplaceQuizRouter } from "./WorkplaceQuizRouter";

export function WorkplaceQuizContainer() {
  const { shiftId } = useParams<{ shiftId: string }>();

  const queryClient = useQueryClient();

  const { userId: workerId } = useDefinedWorker();

  const { data: shiftResponse, isLoading: isLoadingShift } = useGetShift(shiftId);
  const workplaceId = shiftResponse?.response.facilityId;

  const { data: quizData, isLoading: isLoadingWorkplaceShift } = useGetWorkplaceQuiz(
    workplaceId ?? "",
    {
      enabled: isDefined(workplaceId),
    }
  );
  const quiz = quizData?.data[0];
  const isLoadingQuiz = isLoadingShift || isLoadingWorkplaceShift;
  const facilityNotes = isDefined(quizData?.included)
    ? quizData.included
        .map((noteResponse) => ({
          isVisibleToWorker: noteResponse.attributes.isVisibleToWorker,
          categoryTextVisibleToWorker: noteResponse.attributes.categoryTextVisibleToWorker,
          noteId: noteResponse.attributes.noteId ?? undefined,
          note: noteResponse.attributes.note ?? undefined,
          identifier: noteResponse.attributes.identifier ?? undefined,
        }))
        .filter(
          ({ note, isVisibleToWorker, identifier }) =>
            isVisibleToWorker &&
            identifier !== WorkplaceNoteIdentifier.MANDATORY_BREAK_POLICY &&
            identifier !== WorkplaceNoteIdentifier.REST_BREAK_POLICY &&
            isDefined(note) &&
            note.trim().length > 0
        )
    : [];
  const quizQuestions = quiz?.attributes.questions ?? [];

  const {
    mutateAsync: submitQuiz,
    isLoading: isLoadingSubmit,
    isError: isErrorSubmit,
    data: submitQuizResponse,
  } = useSubmitWorkplaceQuiz();
  const userPassedQuiz = submitQuizResponse?.data.data.attributes.status === "PASSED";

  const {
    refetch: refetchBookability,
    isLoading: isFetchingBookabilityStatus,
    data: bookabilityStatus,
  } = useBookabilityStatus({ workerId, shiftIds: [shiftId] }, { enabled: false });
  const stillMissingRequirements =
    bookabilityStatus?.data[0].attributes.bookability.decision === BookabilityDecision.BLOCKED;

  return (
    <ThemeProvider theme={getTheme()}>
      <WorkplaceQuizRouter
        shiftId={shiftId}
        workplaceId={workplaceId}
        facilityNotes={facilityNotes}
        isLoadingQuiz={isLoadingQuiz}
        quizQuestions={quizQuestions}
        isLoadingResult={isLoadingSubmit || isFetchingBookabilityStatus}
        isErrorResult={isErrorSubmit}
        stillMissingRequirements={stillMissingRequirements}
        userPassedQuiz={userPassedQuiz}
        submitQuiz={async (chosenOptions: Record<string, string>) => {
          await submitQuiz({
            workplaceId: workplaceId ?? "",
            quizId: quiz?.id ?? "",
            answers: chosenOptions,
          });
          await queryClient.invalidateQueries([GET_BOOKABILITY_STATUS_URL]);
          await refetchBookability();
        }}
      />
    </ThemeProvider>
  );
}
